import type { Command } from '@o2d/history';
import type { BoardState } from '../state/board-state.js';
import type { BoardObject } from '../interfaces/board-object.interface.js';
import type { TextBox } from '../../features/textbox/textbox.interface.js';
import type { TextDocument } from '../../features/textbox/text-document.type.js';
import { textToDocument } from '../../features/textbox/text-document-migration.js';

/**
 * Union of every mutable property name across every BoardObject variant.
 * Structural fields (id, type) are excluded. The conditional-distributive
 * form ensures type-specific fields like `color` (sticky) or `fill` (rect)
 * are included even though they don't exist on every variant.
 *
 * `paddingV`/`paddingH` (`TextBox`) are ALSO excluded (review 04, MINOR,
 * Linse B): this generic per-property command must never be the thing that
 * changes them (see `TextBox.paddingV`/`paddingH` docs for the one
 * deliberate exception — "Auf 100% normalisieren" — which bypasses this
 * command entirely via `scaleObjectBy`/`state.replaceObject`) — excluding
 * them here makes that a compile error to violate via this generic command,
 * instead of relying only on no call site happening to use it.
 */
export type MutableObjectKey = Exclude<
  BoardObject extends infer T ? (T extends BoardObject ? keyof T : never) : never,
  'id' | 'type' | 'paddingV' | 'paddingH'
>;

/**
 * Change a single property on a single object. Callers provide a loosely-
 * typed value because the set of valid keys/values depends on the object
 * variant; the board element's `updateSelectedProperty<K>` wrapper keeps
 * consumer call sites type-safe.
 *
 * Apply captures the previous value; revert restores it.
 *
 * PRD-RTE-TIPTAP-document-model §T2.4 — text→document normalizer:
 * when the target is a TextBox and the key is `text`, the command also
 * derives a TipTap JSON document from the new text via `textToDocument`
 * and writes it to `obj.document`. Revert restores both fields atomically.
 */
export class UpdatePropertyCommand implements Command<BoardState> {
  readonly label: string;
  private previousValue: unknown;
  private previousDocument: TextDocument | undefined;
  private capturedPrevious = false;

  constructor(
    private readonly id: string,
    private readonly key: MutableObjectKey,
    private readonly value: unknown,
  ) {
    this.label = `${String(key)} ändern`;
  }

  // PRD-009 serializer accessors — read-only views on construction state
  // plus the previousValue captured during apply().
  get objectId(): string {
    return this.id;
  }
  get propertyKey(): string {
    return String(this.key);
  }
  get newValue(): unknown {
    return this.value;
  }
  get previousCapturedValue(): unknown {
    return this.previousValue;
  }
  get hasCapturedPrevious(): boolean {
    return this.capturedPrevious;
  }

  apply(state: BoardState): void {
    const obj = state.getObject(this.id);
    if (!obj) return;
    const record = obj as unknown as Record<string, unknown>;
    // Capture previous value for revert; may be undefined for optional
    // fields (e.g. borderStyle) that haven't been set on this object yet.
    this.previousValue = record[this.key];
    this.capturedPrevious = true;

    if (this.isTextNormalizationCase(obj)) {
      const tb = obj as TextBox;
      this.previousDocument = tb.document;
      const newDocument = textToDocument(this.value as string, tb.listType);
      state.updateObject({ ...obj, text: this.value, document: newDocument } as BoardObject);
      return;
    }

    state.updateObject({ ...obj, [this.key]: this.value } as BoardObject);
  }

  revert(state: BoardState): void {
    const obj = state.getObject(this.id);
    if (!obj || !this.capturedPrevious) return;

    if (this.isTextNormalizationCase(obj) && this.previousDocument !== undefined) {
      state.updateObject({
        ...obj,
        text: this.previousValue,
        document: this.previousDocument,
      } as BoardObject);
      return;
    }

    state.updateObject({ ...obj, [this.key]: this.previousValue } as BoardObject);
  }

  // The text→document normalizer fires only when patching a TextBox's `text`
  // field with a string value. Other object variants (sticky note, etc.) keep
  // their plain `text` semantics and bypass the TipTap migration shim.
  private isTextNormalizationCase(obj: BoardObject): boolean {
    return this.key === 'text' && obj.type === 'text' && typeof this.value === 'string';
  }
}
